import React, { useEffect, useMemo, useState } from 'react';
import { useAdContext } from '../../../store/contexts/ad';
import { GalleryUpload } from '@icarros/torque-react';
import { SaveFormContent } from './SaveFormContent';
import { GalleryWrapper, OpenEditTitleWrapper, OpenEditWrapper } from './style';
import { Body, Title } from '@icarros/ids-react';
import { useAd } from '../../../store/hooks/useAd';
import { getDefaultImages } from '../../../utils/getDefaultImages';
import { Loading } from '../../../components/Loading';

interface Image {
  action?: string;
  index: number;
  ordem: number;
  id: number;
  initial?: boolean;
  src?: string;
  file: File;
}

function normalizeMaximumPhotos(minimumPhotos: number) {
  const CURRENT_MAXIMUM_PHOTOS = 9;
  if (minimumPhotos > CURRENT_MAXIMUM_PHOTOS) return CURRENT_MAXIMUM_PHOTOS;
  return minimumPhotos;
}

export function PhotosForm(): JSX.Element {
  const { currentAd, setValueIsDifferentFromCurrent, formState, setCurrentWarningMessage } =
    useAdContext();
  const { setNewImages, loading, setCurrentContent, initialLoading, getCurrentContent, error } =
    useAd();
  const { fotos } = currentAd || {};
  const [images, setImages] = useState<Image[] | undefined>();
  const defaultImages = getDefaultImages(fotos);

  const minimumPhotos = useMemo(() => {
    if (initialLoading) return;
    if (formState.minimumPhotos) {
      return Number(formState.minimumPhotos.value);
    }
    if (error) return;
    getCurrentContent('getPhotosFeatureFlag', 'SET_MINIMUM_PHOTOS');
  }, [formState, getCurrentContent, initialLoading, error]);

  const currentMinimumPhotos =
    isNaN(Number(minimumPhotos)) || typeof minimumPhotos === 'undefined'
      ? false
      : normalizeMaximumPhotos(minimumPhotos);
  const isCurrentMinimumPhotosNumber = typeof currentMinimumPhotos === 'number';

  const shouldDisable = useMemo(
    () =>
      !images ||
      !images?.find(
        image => (image.action === 'upload' && !image.initial) || (image.action && image.initial)
      ),

    [images]
  );

  const hasMinimumRequiredPhotos = useMemo(() => {
    const defaultImagesLength = defaultImages ? defaultImages.length : 0;
    const imagesLength =
      typeof images !== 'undefined'
        ? images.filter(image => !image.action || image.action === 'upload').length
        : defaultImagesLength;

    if (!isCurrentMinimumPhotosNumber) return true;

    if (currentMinimumPhotos > imagesLength) return true;

    return false;
  }, [images, defaultImages, currentMinimumPhotos, isCurrentMinimumPhotosNumber]);

  useEffect(() => {
    if (!isCurrentMinimumPhotosNumber) {
      return setValueIsDifferentFromCurrent(false);
    }
    if (images) {
      return setValueIsDifferentFromCurrent(!shouldDisable);
    }
    return setValueIsDifferentFromCurrent(false);
  }, [
    images,
    setValueIsDifferentFromCurrent,
    shouldDisable,
    currentMinimumPhotos,
    isCurrentMinimumPhotosNumber
  ]);

  useEffect(() => {
    if (hasMinimumRequiredPhotos && isCurrentMinimumPhotosNumber) {
      setCurrentWarningMessage({
        title: 'você precisa adicionar mais fotos',
        body: `Você ainda não colocou o número mínimo de fotos. Adicione pelo menos ${
          isCurrentMinimumPhotosNumber && currentMinimumPhotos
        } fotos. Ao continuar, sua alteração não será salva`
      });
    } else {
      setCurrentWarningMessage(undefined);
    }
  }, [
    setCurrentWarningMessage,
    hasMinimumRequiredPhotos,
    currentMinimumPhotos,
    isCurrentMinimumPhotosNumber
  ]);

  function PhotosSectionTitle() {
    if (!isCurrentMinimumPhotosNumber || currentMinimumPhotos === 0)
      return <>adicione as fotos do seu carro</>;
    return (
      <>
        adicione pelo menos {currentMinimumPhotos} foto{currentMinimumPhotos > 1 ? 's' : ''}
      </>
    );
  }

  return (
    <OpenEditWrapper>
      <OpenEditTitleWrapper>
        <Title size="overline">editar fotos</Title>
        <Title variant="default" contrast="light">
          <PhotosSectionTitle />
        </Title>
        <Body size="body_2" contrast="default" weight="regular" variant="base">
          Para incluir as fotos, selecione uma de cada vez. Capriche nos cliques para atrair
          compradores.
        </Body>
      </OpenEditTitleWrapper>
      <GalleryWrapper>
        <GalleryUpload
          inputClassNamePrefix="ipt_qa_imagem"
          defaultImages={defaultImages}
          isRebranding
          onChange={setImages}
        />
      </GalleryWrapper>

      {loading && <Loading backgroundOnly />}

      <SaveFormContent
        loading={loading}
        isDisabled={shouldDisable || hasMinimumRequiredPhotos}
        handleClick={() => {
          const filteredImages = defaultImages?.filter(image =>
            images?.find(
              item => item.src?.includes(String(image.id)) && item.initial && !item.action
            )
          );
          const hasUpload = images?.find(image => image.action === 'upload');
          const hasDeleted = images?.find(image => image.action === 'deleted' && image.initial);

          if (hasDeleted) {
            setCurrentContent(
              {
                fotos: filteredImages
              },
              !hasUpload
            );
          }
          if (images && hasUpload) {
            setNewImages(images);
          }
        }}
      />
    </OpenEditWrapper>
  );
}
